import React, {useEffect, useState} from "react";
import {Tooltip, Badge} from "antd";
import {DeleteOutlined, TeamOutlined} from "@ant-design/icons";
import {useTranslation} from "react-i18next";
import moment from "moment";
import {CustomButton} from "../../_defaultComponents/CustomButton/CustomButton";
import {useStore} from "../../../hooks/useStore";
import {ShiftSuggestionTablePropTypes} from "./ShiftSuggestionComponentPropTypes";
import {TableContainer, ActionButtons} from "./ComponentStyles";
import ShiftSuggestionUpdateModal from "./ShiftSuggestionUpdateModal/ShiftSuggestionUpdateModal";
import {useFetch} from "../../../hooks/useFetch";
import {EmptyMessage} from "../../_defaultComponents/EmptyMessage/EmptyMessage";

const ShiftSuggestionTable = ({
    data,
    loading,
    setReloadData,
    currentPage,
    setCurrentPage,
    dataCount,
    setRefresh
}: ShiftSuggestionTablePropTypes) => {
    const {t} = useTranslation();
    const stores = useStore();
    const [deleteConfirmLoading, setDeleteConfirmLoading] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);

    const onClickDeleteButton = async id => {
        stores.appStore.openConfirmModal({
            title: t("modal.confirmTitle", {ns: "common"}),
            content: t("other.shiftSuggestionDeleteConfirmContent", {ns: "common"}),
            onOk: async () => {
                setDeleteConfirmLoading(id);
                await stores.shiftSuggestionStore.deleteShiftSuggestion({_id: id});
                setReloadData();
                setDeleteConfirmLoading(false);
            }
        });
    };

    const [, QuickReactionData, ,] = useFetch({
        fetchFn: stores.shiftStore.getQuickReactionCourierList.bind(null, {
            date: moment(selectedItem?.date).format("YYYY-MM-DD"),
            city: selectedItem?.warehouse.city,
            forecastId: selectedItem?._id
        }),
        condition: !!selectedItem?._id,
        dependencies: [selectedItem]
    });

    useEffect(() => {
        if (data) {
            const matchedItem = data.find(
                item => item._id.toString() === selectedItem?._id.toString()
            );
            setSelectedItem(matchedItem);
        }
    }, [data]);

    const closeUpdateModal = (reloadData = false) => {
        if (reloadData) setReloadData();
        setSelectedItem();
    };

    const renderActionButtons = (id, item) => {
        const waitingApprove = item.preferences.filter(p => p.status == "waitingApprove").length;
        return (
            <ActionButtons>
                <Tooltip title={t("actions.delete", {ns: "common"})}>
                    <CustomButton
                        data-testid="deleteButton"
                        disabled={deleteConfirmLoading}
                        loading={deleteConfirmLoading === id}
                        onClick={() => onClickDeleteButton(id)}
                        icon={<DeleteOutlined />}
                    ></CustomButton>
                </Tooltip>

                {
                    <Tooltip title={t("actions.update", {ns: "common"})}>
                        <Badge count={waitingApprove} showZero={false} offset={[-10, 5]}>
                            <CustomButton
                                data-testid="updateButton"
                                disabled={deleteConfirmLoading}
                                loading={deleteConfirmLoading === id}
                                onClick={() => setSelectedItem(item)}
                                icon={<TeamOutlined />}
                            ></CustomButton>
                        </Badge>
                    </Tooltip>
                }
            </ActionButtons>
        );
    };

    const getStatusCount = (preferences, status) => {
        const statusCount = preferences.filter(p => p.status == status).length;
        let result = "-";
        if (statusCount > 0) {
            result = statusCount;
        }
        return result;
    };

    return (
        <>
            <ShiftSuggestionUpdateModal
                id={selectedItem?._id}
                data={selectedItem}
                visibility={Boolean(selectedItem?._id)}
                onCancel={closeUpdateModal}
                quickReactionData={QuickReactionData?.result?.rows}
                forecastId={selectedItem?._id}
                setRefresh={setRefresh}
            />
            <TableContainer
                pagination={{
                    total: dataCount,
                    current: currentPage,
                    onChange: async page => {
                        setCurrentPage(page);
                    },
                    hideOnSinglePage: true,
                    showSizeChanger: false
                }}
                rowClassName={record =>
                    record.plannedCourierCount == getStatusCount(record.preferences, "approved")
                        ? "resolved"
                        : ""
                }
                rowKey="_id"
                dataSource={data}
                data-testid="shiftSuggestionTable"
                loading={loading}
                locale={{
                    emptyText: <EmptyMessage />
                }}
            >
                <TableContainer.Column
                    dataIndex="date"
                    title={t("fields.date", {ns: "common"})}
                    render={date => (
                        <span data-testid="date">
                            {!date ? "-" : moment(date).format("DD.MM.YYYY")}
                        </span>
                    )}
                />
                <TableContainer.Column
                    dataIndex="warehouse"
                    title={t("fields.warehouse", {ns: "common"})}
                    render={warehouse => <span data-testid="warehouse">{warehouse.name}</span>}
                />
                <TableContainer.Column
                    dataIndex="warehouse"
                    title={t("fields.company", {ns: "common"})}
                    render={warehouse => (
                        <span data-testid="companyName">{warehouse.companyName}</span>
                    )}
                />
                <TableContainer.Column
                    dataIndex="warehouse"
                    title={t("fields.city", {ns: "common"})}
                    render={warehouse => (
                        <span data-testid="city">{warehouse.city?.capitalize()}</span>
                    )}
                />
                <TableContainer.Column
                    dataIndex="startTime"
                    title={t("fields.shiftStartTime", {ns: "common"})}
                    render={date => (
                        <span data-testid="date">{!date ? "-" : moment(date).format("HH:mm")}</span>
                    )}
                />
                <TableContainer.Column
                    dataIndex="endTime"
                    title={t("fields.shiftEndTime", {ns: "common"})}
                    render={date => (
                        <span data-testid="date">{!date ? "-" : moment(date).format("HH:mm")}</span>
                    )}
                />
                <TableContainer.Column
                    dataIndex="plannedCourierCount"
                    title={t("plannedCount", {ns: "shiftSuggestion"})}
                    render={plannedCourierCount => (
                        <span data-testid="plannedCourierCount">{plannedCourierCount}</span>
                    )}
                />
                <TableContainer.Column
                    dataIndex="preferences"
                    title={t("approvedCount", {ns: "shiftSuggestion"})}
                    render={preferences => (
                        <span data-testid="approvedCourierCount">
                            {getStatusCount(preferences, "approved")}
                        </span>
                    )}
                />
                <TableContainer.Column
                    dataIndex="preferences"
                    title={t("rejectedCount", {ns: "shiftSuggestion"})}
                    render={preferences => (
                        <span data-testid="rejectedCourierCount">
                            {getStatusCount(preferences, "rejected")}
                        </span>
                    )}
                />
                <TableContainer.Column
                    title={t("actions.action", {ns: "common"})}
                    dataIndex="_id"
                    render={renderActionButtons}
                />
            </TableContainer>
        </>
    );
};

export default ShiftSuggestionTable;
